/**
 * `NewTemplateDialog` — TH-55362.
 *
 * Modal de "Novo template" usado pelo botão "+ Novo template" em
 * `/templates`. Form mínimo (nome + família + área opcional) →
 * POST /api/templates → callback `onCreated(id)` para o parent
 * navegar até `/templates/:id` (TemplateEditPage), onde o operador
 * refina html/css/ai_prompt.
 *
 * Decisões:
 *
 * - Form mínimo. O backend exige `html`/`css`/`ai_prompt` não-vazios
 *   (schemas/template.py: `min_length=1`). Como não temos editor
 *   completo aqui, preenchemos com scaffolds neutros — o operador
 *   edita em seguida no TemplateEditPage. Defaults espelham o padrão
 *   usado em testes do worker (test_generate_art_task.py):
 *     html: "<h1>{{ product.name }}</h1>"
 *     css:  "body{color:{{ tenant.primary_color }}}"
 *     ai_prompt: "fundo neutro"
 *   Isso garante 201 sem inventar campos.
 *
 * - `family` não é coluna no schema — é derivada de `tags` (ver
 *   TemplatesPage.familyOf). Convertemos a escolha do select em
 *   `tags: ["cartazista"]` (cartazista) ou `tags: []` (clean,
 *   default).
 *
 * - `area_id` é opcional. Carregamos a lista de áreas; se a query
 *   falhar, escondemos o select (permite criar template sem área).
 *
 * - Sem `useNavigate` dentro do componente — callback `onCreated`
 *   no parent navega. Mesmo padrão de NewCampaignDialog (TH-55143),
 *   isola o teste do router.
 *
 * - Sem toast lib; feedback de erro inline (mesmo padrão).
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Select } from "@/components/ui/Select";

export interface NewTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Chamado com o id do template recém-criado para navegação no parent. */
  onCreated: (templateId: string) => void;
}

type Family = "clean" | "cartazista";

interface Area {
  id: string;
  name: string;
  slug: string;
}

interface AreasResponse {
  data: Area[];
  meta: { total: number };
}

interface CreatedTemplate {
  id: string;
}

const NO_AREA = "__none__";

// Scaffolds neutros — operador edita no TemplateEditPage. Mantém
// backend feliz (min_length=1) sem inventar conteúdo de marca aqui.
const DEFAULT_HTML = "<h1>{{ product.name }}</h1>";
const DEFAULT_CSS = "body{color:{{ tenant.primary_color }}}";
const DEFAULT_AI_PROMPT = "fundo neutro";

async function fetchAreas(): Promise<AreasResponse> {
  const res = await fetch("/api/areas?page=1&per_page=100", {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as AreasResponse;
}

interface CreatePayload {
  name: string;
  html: string;
  css: string;
  ai_prompt: string;
  tags: string[];
  area_id?: string | null;
}

async function createTemplate(payload: CreatePayload): Promise<CreatedTemplate> {
  const res = await fetch("/api/templates", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: unknown };
    const detail =
      typeof body.detail === "string"
        ? body.detail
        : `Falha ao criar template (HTTP ${res.status}).`;
    throw new Error(detail);
  }
  return (await res.json()) as CreatedTemplate;
}

export function NewTemplateDialog({
  open,
  onOpenChange,
  onCreated,
}: NewTemplateDialogProps) {
  const [name, setName] = useState("");
  const [family, setFamily] = useState<Family>("clean");
  const [areaId, setAreaId] = useState<string>(NO_AREA);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const areasQuery = useQuery({
    queryKey: ["areas", "for-new-template-dialog"],
    queryFn: fetchAreas,
    staleTime: 5 * 60_000,
    enabled: open,
    retry: false,
  });

  const areas = areasQuery.data?.data ?? [];
  const showAreaField = areasQuery.isSuccess && areas.length > 0;

  function reset() {
    setName("");
    setFamily("clean");
    setAreaId(NO_AREA);
    setError(null);
  }

  function handleOpenChange(next: boolean) {
    if (submitting) return;
    if (!next) reset();
    onOpenChange(next);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Informe um nome para o template.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload: CreatePayload = {
        name: trimmed,
        html: DEFAULT_HTML,
        css: DEFAULT_CSS,
        ai_prompt: DEFAULT_AI_PROMPT,
        tags: family === "cartazista" ? ["cartazista"] : [],
      };
      if (areaId !== NO_AREA) {
        payload.area_id = areaId;
      }
      const created = await createTemplate(payload);
      reset();
      onOpenChange(false);
      onCreated(created.id);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Falha ao criar template.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogTitle>Novo template</DialogTitle>
        <DialogDescription>
          Crie o registro e ajuste o conteúdo na próxima tela.
        </DialogDescription>

        <form
          onSubmit={(e) => void handleSubmit(e)}
          className="mt-4 space-y-4"
          data-testid="new-template-form"
        >
          <div className="space-y-1.5">
            <Label htmlFor="new-template-name">Nome</Label>
            <Input
              id="new-template-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex.: Cartazista — açougue"
              autoFocus
              disabled={submitting}
              required
              maxLength={200}
              data-testid="new-template-name"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="new-template-family">Família</Label>
            <Select
              id="new-template-family"
              value={family}
              onChange={(e) => setFamily(e.target.value as Family)}
              disabled={submitting}
              data-testid="new-template-family"
            >
              <option value="clean">Clean</option>
              <option value="cartazista">Cartazista</option>
            </Select>
            <p className="text-xs text-[var(--color-muted-foreground)]">
              Cartazista usa o estilo de encarte; Clean é o padrão da casa.
            </p>
          </div>

          {showAreaField ? (
            <div className="space-y-1.5">
              <Label htmlFor="new-template-area">Área (opcional)</Label>
              <Select
                id="new-template-area"
                value={areaId}
                onChange={(e) => setAreaId(e.target.value)}
                disabled={submitting}
                data-testid="new-template-area"
              >
                <option value={NO_AREA}>Sem área</option>
                {areas.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}

          {error ? (
            <p
              role="alert"
              className="rounded-md border border-[var(--color-destructive)] bg-[var(--color-destructive)]/10 px-3 py-2 text-sm text-[var(--color-destructive)]"
              data-testid="new-template-error"
            >
              {error}
            </p>
          ) : null}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={submitting || !name.trim()}
              data-testid="new-template-submit"
            >
              {submitting ? "Criando…" : "Criar template"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
